<template>
  <div class="col-md-10 col-sm-9 u-pad-all-25 c-common-box-shadow u-bg-color-fff configChatVue">
    <h3 class="u-cmn-botmargin">Chat</h3>
    <p class="o-blue-regular-text">
      Chat for {{ clientName }}: whether it is on, when people can post, and which
      channels exist. Membership comes from CS3, so the groups fill themselves.
    </p>

    <div class="cc__preview" data-testid="cc-preview-banner">
      <i class="fa fa-flask" aria-hidden="true"></i>
      <span>
        <strong>Preview.</strong> The chat service is not built yet, so everything here is
        example data and <strong>nothing is saved</strong>. Switches move so the screen can be
        reviewed; reload and they return to where they were.
      </span>
    </div>

    <!-- ── the on/off switch ─────────────────────────────────────────────── -->
    <div class="cc__master" data-testid="cc-master">
      <div class="cc__rowmain">
        <div class="cc__rowt">Chat for {{ clientName }}</div>
        <div class="cc__rows">
          {{ settings.chatOn
            ? 'On — the panel is available to everyone whose group is switched on'
            : 'Off — the panel and the app’s chat item are gone for everyone in this client' }}
        </div>
      </div>
      <button type="button" class="cc__sw" role="switch" data-testid="cc-chat-on"
              :aria-checked="String(settings.chatOn)" @click="settings.chatOn = !settings.chatOn"></button>
    </div>

    <div v-if="!settings.chatOn" class="cc__off" data-testid="cc-off-note">
      <strong>Chat is off for {{ clientName }}.</strong>
      Nothing below applies while it is off. Groups, office hours and the people named
      individually keep their settings and take effect again the moment chat is switched
      back on — nothing is lost by turning it off, and the conversation log stays in
      Reports for its 90 days.
    </div>

    <div v-else class="cc__cols">
      <!-- ── left: office hours, then who may post outside them ──────────── -->
      <div class="cc__col">
        <section class="cc__card">
          <h4>Office hours</h4>
          <p class="cc__lead">
            When the client is closed, chat is read-only: people can read and scroll, but not
            post. Nothing is hidden and nothing is deleted.
          </p>

          <div class="cc__master cc__master--sub">
            <div class="cc__rowmain">
              <div class="cc__rowt">Use a schedule</div>
              <div class="cc__rows">
                {{ hours.on ? openDaysSummary + ' · ' + hours.tz : 'Off — chat is open whenever chat itself is on' }}
              </div>
            </div>
            <button type="button" class="cc__sw" role="switch" data-testid="cc-hours-on"
                    :aria-checked="String(hours.on)" @click="hours.on = !hours.on"></button>
          </div>

          <template v-if="hours.on">
            <div class="cc__tz">
              <span class="cc__tzl">Timezone</span>
              <code>{{ hours.tz }}</code>
              <span class="cc__tzs">{{ hours.tzFromClient ? 'from the client record' : 'fallback — this client has no timezone set' }}</span>
            </div>

            <div class="cc__tw">
              <table class="cc__t">
                <thead><tr><th>Day</th><th>Open</th><th>From</th><th>To</th></tr></thead>
                <tbody>
                  <tr v-for="d in hours.days" :key="d.key" :class="{ 'cc__dim': !d.open }">
                    <td class="cc__nm">{{ d.label }}</td>
                    <td>
                      <button type="button" class="cc__sw cc__sw--sm" role="switch"
                              :aria-checked="String(d.open)" :aria-label="d.label"
                              @click="d.open = !d.open"></button>
                    </td>
                    <td v-if="d.open"><input type="time" class="cc__time" v-model="d.from" step="300"></td>
                    <td v-if="d.open"><input type="time" class="cc__time" v-model="d.to" step="300"></td>
                    <td v-if="!d.open" colspan="2" class="cc__closed">Closed</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- #2091 r5 — Ed, 17:33: "Schedule Only No Holidays". This is a
                 DECISION, not a gap waiting on one, so "yet" and "Flagged for
                 Ed" are both wrong: the schedule is weekly and that is the whole
                 of it. -->
            <p class="cc__hint">
              The schedule is weekly only — there is no holiday calendar. A statutory holiday
              is an ordinary open day, so close it on the day if the office is shut.
            </p>

            <h5 class="cc__sub">Allowed to post outside office hours</h5>
            <p class="cc__lead">
              A role switch covers everyone in that role. Name individual people on the right
              when the role switch is the wrong shape.
            </p>

            <selectBar :count="rolesAllowed" :total="roleKeys.length" noun="roles allowed"
                       @all="setAllRoles(true)" @none="setAllRoles(false)" />

            <div class="cc__tw">
              <table class="cc__t">
                <!-- #2091 r5 — Ed, 17:31, verbatim: "Allowed to post outside
                     office hours". The column said "Outside hours", which reads
                     as the hours themselves rather than as permission. -->
                <thead><tr><th>Role</th><th>Who it covers</th><th class="cc__r">Allowed to post outside office hours</th></tr></thead>
                <tbody>
                  <tr v-for="k in roleKeys" :key="k" :class="{ 'cc__dim': !hours.roles[k] }">
                    <td class="cc__nm">{{ rolePlural[k] }}</td>
                    <td class="cc__sublabel">{{ roleCovers[k] }}</td>
                    <td class="cc__r">
                      <button type="button" class="cc__sw" role="switch" :data-testid="'cc-role-' + k"
                              :aria-checked="String(hours.roles[k])"
                              :aria-label="rolePlural[k] + ' allowed to post outside office hours'"
                              @click="$set(hours.roles, k, !hours.roles[k])"></button>
                    </td>
                  </tr>
                  <tr class="cc__dim">
                    <td class="cc__nm">CallSteward staff</td>
                    <td class="cc__sublabel">CS Admin and CS Support</td>
                    <td class="cc__r">
                      <button type="button" class="cc__sw" role="switch" aria-checked="true"
                              disabled aria-label="CallSteward staff, always allowed to post outside office hours"></button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="cc__hint">CallSteward staff are never restricted by a client’s schedule.</p>
          </template>
        </section>

        <!-- ── channel groups ─────────────────────────────────────────────── -->
        <section class="cc__card">
          <h4>Channel groups</h4>
          <p class="cc__lead">
            Which groups exist for this client. Membership comes from CS3 — roles, skills,
            statuses and job calls — so a group fills itself and stays current.
          </p>

          <selectBar :count="groupsOn" :total="groupKeys.length" noun="groups on"
                     @all="setAllGroups(true)" @none="setAllGroups(false)" />

          <div class="cc__tw">
            <table class="cc__t">
              <thead><tr><th>Group</th><th>Membership comes from</th><th class="cc__r">On</th></tr></thead>
              <tbody>
                <tr v-for="k in groupKeys" :key="k" :class="{ 'cc__dim': !settings.groups[k] }">
                  <td class="cc__nm">
                    {{ k }}
                    <span v-if="masterSwitches[k]" class="cc__master-tag">master</span>
                  </td>
                  <td class="cc__sublabel">
                    {{ groupSource[k] }}
                    <!-- #2091 r3 — Ed: "you need both to have a channel". Said
                         beside the switch, because a switch that only half
                         creates something has to say so where it is flicked. -->
                    <div v-if="masterSwitches[k]" class="cc__both">{{ masterSwitches[k] }}</div>
                  </td>
                  <td class="cc__r">
                    <button type="button" class="cc__sw" role="switch" :aria-label="k"
                            :aria-checked="String(settings.groups[k])"
                            @click="$set(settings.groups, k, !settings.groups[k])"></button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="cc__hint">
            Switching a group off hides its channels from the panel. The conversation is kept
            and stays in Reports.
          </p>
          <p class="cc__hint">
            <strong>Job channels are time-boxed.</strong> A job's channel is open from
            <strong>24 hours before</strong> the job starts until <strong>24 hours after</strong> it
            ends. Outside that it stays readable but nobody can post.
          </p>

          <!-- #2091 r3 — "you need both to have a channel" is a rule whose effect
               is an ABSENCE, and an absent channel is invisible. This table makes
               it visible: the master switch above, each item's own Chat option,
               and the channel that does or does not result. -->
          <h5 class="cc__sub">Where job and employer channels come from</h5>
          <p class="cc__lead">
            The switches above <em>permit</em> these channels. Each job and each employer also has
            its own Chat option, set on the job or the employer. A channel exists only where both
            are on.
          </p>
          <div class="cc__tw">
            <table class="cc__t">
              <thead><tr><th>Job or employer</th><th>Client switch</th><th>Its own Chat option</th><th>Channel?</th></tr></thead>
              <tbody>
                <tr v-for="row in bothSwitchRows" :key="row.id" :class="{ 'cc__dim': !row.channel }">
                  <td class="cc__nm">{{ row.label }}</td>
                  <td class="cc__sublabel">{{ row.master ? 'on' : 'off' }}</td>
                  <td class="cc__sublabel">{{ row.own ? 'on' : 'off' }}</td>
                  <td :class="row.channel ? 'cc__yes' : 'cc__no'">
                    {{ row.channel ? 'yes' : 'no channel' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="cc__hint">
            Turn the <strong>Job</strong> or <strong>Employers / Payroll</strong> switch above off and
            every row under it loses its channel, whatever each item's own option says.
          </p>
        </section>
      </div>

      <!-- ── right: people named individually, then global access ────────── -->
      <div class="cc__col">
        <section class="cc__card">
          <h4>People named individually</h4>
          <p class="cc__lead">
            A person named here overrides their role, either way. Precedence is person, then
            role, then the schedule.
          </p>

          <input type="search" class="cc__search" v-model="search" data-testid="cc-search"
                 placeholder="Search employees — name, phone or email"
                 aria-label="Search employees to name individually">

          <template v-if="search.trim()">
            <p v-if="!hits.length" class="cc__hint">
              No employee matches “{{ search.trim() }}” who is not already named below.
            </p>
            <template v-else>
              <selectBar :count="pickedCount" :total="hits.length" noun="found"
                         @all="pickAll(true)" @none="pickAll(false)" />
              <div class="cc__tw">
                <table class="cc__t">
                  <thead><tr><th class="cc__tick"></th><th>Name</th><th>employee_id</th><th>Role</th><th>Phone</th><th>Email</th></tr></thead>
                  <tbody>
                    <tr v-for="d in hits" :key="d.eid" class="cc__pick" :class="{ 'cc__on': !!picked[d.eid] }"
                        role="button" tabindex="0" :aria-pressed="String(!!picked[d.eid])"
                        @click="togglePick(d)" @keydown.space.prevent="togglePick(d)" @keydown.enter.prevent="togglePick(d)">
                      <td class="cc__tick"><span class="cc__box" aria-hidden="true"></span></td>
                      <td class="cc__nm">{{ d.name }}</td>
                      <td class="cc__mono">{{ d.eid }}</td>
                      <td class="cc__sublabel">{{ roleLabel[d.role] }}</td>
                      <td class="cc__mono">{{ d.phone }}</td>
                      <td class="cc__mono">{{ d.email }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <button v-if="pickedCount" type="button" class="c-btn cc__btn" data-testid="cc-add"
                      @click="addPicked()">
                Add {{ pickedCount }} {{ pickedCount === 1 ? 'person' : 'people' }} to the list
              </button>
            </template>
          </template>

          <h5 class="cc__sub">Named for this client</h5>
          <p v-if="!hours.people.length" class="cc__hint">
            Nobody is named individually. Everyone follows their role’s switch.
          </p>
          <template v-else>
            <selectBar :count="peopleAllowed" :total="hours.people.length" noun="allowed"
                       @all="setAllPeople(true)" @none="setAllPeople(false)" />
            <div class="cc__tw">
              <table class="cc__t">
                <thead><tr><th>Name</th><th>employee_id</th><th>Role</th><th>Allowed to post outside office hours</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="(x, i) in hours.people" :key="x.eid" :class="{ 'cc__dim': !x.allowed }">
                    <td class="cc__nm">{{ x.name }}</td>
                    <td class="cc__mono">{{ x.eid }}</td>
                    <td class="cc__sublabel">{{ roleLabel[x.role] }}</td>
                    <td>
                      <div class="cc__swwrap">
                        <button type="button" class="cc__sw" role="switch"
                                :aria-label="x.name + ' allowed to post outside office hours'"
                                :aria-checked="String(x.allowed)" @click="x.allowed = !x.allowed"></button>
                        <span class="cc__swl">{{ personLabel(x) }}</span>
                      </div>
                    </td>
                    <td class="cc__r">
                      <button type="button" class="cc__rm" @click="hours.people.splice(i, 1)">Remove</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="cc__hint">
              Removing someone does not block them — it returns them to whatever their role allows.
            </p>
          </template>
        </section>

        <section class="cc__card">
          <h4>Access to CS-world channels</h4>
          <p class="cc__lead">
            Only Client Admins, Power Users and Stewards can be let into the global channels,
            and only you can let them in.
          </p>

          <selectBar :count="grantsOn" :total="grantable.length" noun="granted"
                     @all="setAllGrants(true)" @none="setAllGrants(false)" />

          <div class="cc__tw">
            <table class="cc__t">
              <thead><tr><th>Name</th><th>Role</th><th>Global access</th></tr></thead>
              <tbody>
                <tr v-for="g in settings.grants" :key="g.name" :class="{ 'cc__dim': !g.granted }">
                  <td class="cc__nm">{{ g.name }}</td>
                  <td class="cc__sublabel">{{ roleLabel[g.role] }}</td>
                  <td>
                    <div class="cc__swwrap">
                      <button type="button" class="cc__sw" role="switch" :aria-label="g.name"
                              :aria-checked="String(g.granted)" :disabled="g.role === 'member'"
                              @click="g.granted = !g.granted"></button>
                      <span class="cc__swl">{{ grantLabel(g) }}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="cc__hint">
            A grant cancels itself: if someone’s role or client changes in CS3, their global
            access is revoked automatically and you have to grant it again.
          </p>
        </section>
      </div>
    </div>

    <section class="cc__card cc__card--wide">
      <h4>Reading the conversation log</h4>
      <p class="cc__lead">
        You can read your members’ chats within the 90 days chat keeps — in Reports, not in the
        chat panel. The panel only ever shows channels you are in, for everyone including you.
      </p>
      <p class="cc__hint">
        <strong>Reports · Chat log.</strong> By channel, date range and sender, scoped to
        <code>{{ clientName }}</code>. A Power User can pull the same report. Older than 90 days
        is recovered from the SQL backup, not from a report.
      </p>
    </section>
  </div>
</template>

<script>
/**
 * #2091 — Configuration -> Chat, the real screen behind the CHAT_UI_PREVIEW flag.
 *
 * Content follows the approved wireframe (cs3-chat !4 round 4). Rendered with
 * the Configuration area's own classes — `col-md-10 col-sm-9 u-pad-all-25
 * c-common-box-shadow u-bg-color-fff`, `h3.u-cmn-botmargin`,
 * `p.o-blue-regular-text` — so it is the same screen as its neighbours rather
 * than a lookalike; only what the wireframe adds is styled here.
 *
 * ⚠ NOTHING HERE PERSISTS, AND NOTHING IS FETCHED. There is no chat back end
 * (cs3-chat #6-#10, #18). This component imports no service and makes no
 * request: the data comes from `chatPreview.js` and edits live in local state
 * until the page reloads. The banner at the top says so, because a settings
 * screen that silently discards changes is worse than one that admits it.
 */
import selectBar from './chatSelectBar.vue';
import {
    CHANNELS, DIRECTORY, FAKE_CLIENT, GROUP_SOURCE, MASTER_SWITCHES,
    ROLE_COVERS, ROLE_LABEL, ROLE_PLURAL,
    previewStore,
} from '../../chatPreview.js';

export default {
    name: 'configChat',
    components: { selectBar },
    props: ['empDetails'],
    data() {
        return {
            clientName: FAKE_CLIENT,
            // The SHARED preview store, not a private copy: switching chat off
            // here makes the panel disappear, and closing today's hours makes
            // its composer read-only. That is how the wireframe's states are
            // reached in the real app, without mock-up controls.
            settings: previewStore(),
            search: '',
            picked: {},
            // #2091 r5 — Employers beside the other four (Ed, 17:32).
            roleKeys: ['ca', 'pu', 'steward', 'member', 'employer'],
            roleLabel: ROLE_LABEL,
            rolePlural: ROLE_PLURAL,
            roleCovers: ROLE_COVERS,
            groupSource: GROUP_SOURCE,
            masterSwitches: MASTER_SWITCHES,
        };
    },
    computed: {
        hours() { return this.settings.hours; },
        groupKeys() { return Object.keys(this.settings.groups); },
        groupsOn() { return this.groupKeys.filter((k) => this.settings.groups[k]).length; },
        rolesAllowed() { return this.roleKeys.filter((k) => this.hours.roles[k]).length; },
        peopleAllowed() { return this.hours.people.filter((x) => x.allowed).length; },
        /** Members can never be granted, so they are not part of the count. */
        grantable() { return this.settings.grants.filter((g) => g.role !== 'member'); },
        grantsOn() { return this.grantable.filter((g) => g.granted).length; },
        pickedCount() { return this.hits.filter((d) => this.picked[d.eid]).length; },
        hits() {
            const q = this.search.trim().toLowerCase();
            if (!q) return [];
            const already = {};
            this.hours.people.forEach((x) => { already[x.eid] = true; });
            const digits = q.replace(/[^0-9]/g, '');

            return DIRECTORY.filter((d) => {
                if (already[d.eid]) return false;
                if (d.name.toLowerCase().indexOf(q) >= 0) return true;
                if (d.email.toLowerCase().indexOf(q) >= 0) return true;

                return digits.length >= 3 && d.phone.replace(/[^0-9]/g, '').indexOf(digits) >= 0;
            });
        },
        /**
         * #2091 r3 — one row per job/employer channel in the fake data, showing
         * the client master switch, the item's own Chat option, and whether a
         * channel results. Reads the live switches above, so flicking one
         * changes the answers.
         */
        bothSwitchRows() {
            return CHANNELS
                .filter((c) => c.job || c.employer)
                .map((c) => {
                    const group = c.job ? 'Job' : 'Employers / Payroll';
                    const master = this.settings.groups[group] !== false;
                    const own = c.job ? c.job.chatOn : c.employer.chatOn;

                    return {
                        id: c.id,
                        label: c.label.replace(/^(Job · |Employers \/ Payroll · )/, ''),
                        master,
                        own,
                        channel: master && own,
                    };
                });
        },
        openDaysSummary() {
            const open = this.hours.days.filter((d) => d.open).length;

            return open ? open + ' of 7 days open' : 'Closed every day';
        },
    },
    methods: {
        setAllRoles(on) { this.roleKeys.forEach((k) => this.$set(this.hours.roles, k, on)); },
        setAllGroups(on) { this.groupKeys.forEach((k) => this.$set(this.settings.groups, k, on)); },
        setAllPeople(on) { this.hours.people.forEach((x) => { x.allowed = on; }); },
        setAllGrants(on) { this.grantable.forEach((g) => { g.granted = on; }); },
        togglePick(d) {
            if (this.picked[d.eid]) this.$delete(this.picked, d.eid);
            else this.$set(this.picked, d.eid, true);
        },
        pickAll(on) {
            this.hits.forEach((d) => {
                if (on) this.$set(this.picked, d.eid, true);
                else this.$delete(this.picked, d.eid);
            });
        },
        addPicked() {
            this.hits.forEach((d) => {
                if (!this.picked[d.eid]) return;
                // Added NOT allowed: naming someone starts a decision, it does
                // not make it. The Client Admin then says which way.
                this.hours.people.push({ eid: d.eid, name: d.name, role: d.role, allowed: false });
            });
            this.picked = {};
            this.search = '';
        },
        personLabel(x) {
            if (x.allowed) return 'Allowed';

            return this.hours.roles[x.role] ? 'Read-only — overrides their role' : 'Read-only';
        },
        grantLabel(g) {
            if (g.role === 'member') return 'Members can never be granted';

            return g.granted ? 'Granted by you' : 'Not granted';
        },
    },
};
</script>

<style scoped>
.cc__preview{
    display:flex;align-items:flex-start;gap:9px;
    margin:0 0 20px;padding:11px 13px;
    background:#fff6e0;border:1px solid #fb8b03;border-radius:4px;
    font-size:13px;line-height:1.5;color:#6b4a00;
}
.cc__preview i{margin-top:2px;color:#fb8b03}

.cc__master{
    display:flex;align-items:center;gap:12px;
    padding:13px 15px;margin:0 0 18px;
    background:#eef4ff;border:1px solid #c9d9f5;border-radius:4px;
}
.cc__master--sub{margin-bottom:13px}
.cc__rowmain{flex:1;min-width:0}
.cc__rowt{font-weight:600;font-size:14px;color:#222}
.cc__rows{font-size:12px;color:#6b7280;margin-top:2px}
.cc__off{
    padding:13px 15px;background:#fff6e0;border:1px solid #fb8b03;border-radius:4px;
    font-size:13px;line-height:1.55;color:#6b4a00;max-width:80ch;
}

.cc__cols{display:flex;gap:26px;align-items:flex-start;flex-wrap:wrap}
.cc__col{flex:1 1 420px;min-width:0;display:flex;flex-direction:column;gap:22px}
.cc__card{min-width:0}
.cc__card--wide{margin-top:22px;padding-top:20px;border-top:1px solid #dcdfe6}
.cc__card h4{
    margin:0 0 4px;font-size:13px;font-weight:700;text-transform:uppercase;
    letter-spacing:.07em;color:#6b7280;
}
.cc__sub{
    margin:20px 0 4px;padding-top:15px;border-top:1px solid #e8ebf0;
    font-size:10.5px;font-weight:700;text-transform:uppercase;letter-spacing:.085em;color:#6b7280;
}
.cc__lead{margin:0 0 13px;font-size:12.5px;line-height:1.5;color:#6b7280;max-width:62ch}
.cc__hint{
    margin:11px 0 0;padding:8px 10px;background:#f4f7f8;border:1px solid #dcdfe6;
    border-radius:4px;font-size:11.5px;line-height:1.45;color:#6b7280;
}
.cc__hint code{font-size:11px;color:#2451a8;background:none;padding:0}

.cc__tz{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;padding:9px 0 3px;font-size:11.5px;color:#6b7280}
.cc__tzl{font-weight:600;color:#4a5562}
.cc__tz code{font-size:11px;color:#2451a8;background:none;padding:0}
.cc__tzs{color:#8b949e}

.cc__tw{overflow-x:auto}
.cc__t{width:100%;border-collapse:collapse;font-size:12.5px}
.cc__t th{
    text-align:left;padding:7px 10px 7px 0;border-bottom:1px solid #dcdfe6;
    font-size:10px;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;
    font-weight:600;white-space:nowrap;
}
.cc__t td{padding:8px 10px 8px 0;border-bottom:1px solid #e8ebf0;color:#4a5562;vertical-align:middle}
.cc__t tr:last-child td{border-bottom:0}
.cc__t th:last-child,.cc__t td:last-child{padding-right:0}
.cc__nm{color:#222;font-weight:600;white-space:nowrap}
.cc__sublabel{color:#6b7280}
.cc__both{margin-top:3px;font-size:11px;line-height:1.45;color:#9a6a00}
.cc__yes{color:#17a068;font-weight:600}
.cc__no{color:#8b949e}
.cc__master-tag{
    margin-left:6px;padding:1px 5px;border-radius:3px;
    background:#fff6e0;border:1px solid #fb8b03;color:#9a6a00;
    font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:9px;
    font-weight:400;letter-spacing:.03em;text-transform:uppercase;
}
.cc__mono{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11px;color:#6b7280;white-space:nowrap}
.cc__r{text-align:right;white-space:nowrap}
.cc__dim .cc__nm{color:#6b7280}
.cc__closed{color:#8b949e;font-style:italic}
.cc__swwrap{display:flex;align-items:center;gap:9px}
.cc__swl{font-size:11.5px;color:#6b7280}

.cc__pick{cursor:pointer}
.cc__pick:hover td{background:#f6f8fb}
.cc__pick.cc__on td{background:#eef4ff}
.cc__tick{width:26px;padding-right:0}
.cc__box{
    display:block;width:15px;height:15px;border-radius:3px;position:relative;
    border:1px solid #dcdfe6;background:#fff;
}
.cc__pick.cc__on .cc__box{background:#2451a8;border-color:#2451a8}
.cc__pick.cc__on .cc__box::after{
    content:"";position:absolute;left:4px;top:1px;width:4px;height:8px;
    border:solid #fff;border-width:0 2px 2px 0;transform:rotate(42deg);
}

.cc__search{
    width:100%;padding:7px 9px;font-size:12.5px;color:#222;
    background:#fff;border:1px solid #dcdfe6;border-radius:4px;margin-bottom:4px;
}
.cc__search:focus{border-color:#2451a8;outline:none}
.cc__btn{margin-top:12px;background:#2451a8;color:#fff;border:1px solid #2451a8;border-radius:4px;padding:7px 14px;font-size:12.5px;font-weight:600}
.cc__btn:hover{background:#1b3f85;border-color:#1b3f85}
.cc__rm{background:none;border:1px solid #dcdfe6;border-radius:3px;padding:3px 8px;font-size:11px;color:#6b7280}
.cc__rm:hover{border-color:#a8252e;color:#a8252e}

.cc__time{
    font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:11.5px;color:#222;
    padding:4px 5px;border:1px solid #dcdfe6;border-radius:3px;background:#fff;
}
.cc__time:focus{border-color:#2451a8;outline:none}

.cc__sw{
    flex:0 0 38px;width:38px;height:21px;border-radius:11px;position:relative;
    background:#c4ccd8;border:0;padding:0;transition:background .13s;cursor:pointer;
}
.cc__sw::after{
    content:"";position:absolute;top:2px;left:2px;width:17px;height:17px;border-radius:50%;
    background:#fff;transition:transform .13s;box-shadow:0 1px 2px rgba(0,0,0,.26);
}
.cc__sw[aria-checked="true"]{background:#2451a8}
.cc__sw[aria-checked="true"]::after{transform:translateX(17px)}
.cc__sw:disabled{opacity:.42;cursor:not-allowed}
.cc__sw--sm{flex:0 0 32px;width:32px;height:18px}
.cc__sw--sm::after{width:14px;height:14px}
.cc__sw--sm[aria-checked="true"]::after{transform:translateX(14px)}
</style>
